import { motion } from "framer-motion";
import { PencilRuler, Layers, Truck, Sparkles, ArrowUpRight } from "lucide-react";
import { BIRDS } from "@/lib/brand";
import { useLang } from "@/lib/i18n";

export default function Studio() {
  const { t } = useLang();
  const SERVICES = [
    {
      tag: "01 / Pre-Press",
      title: t("studio.s1_title"),
      body: t("studio.s1_body"),
      tex: "/images/studio/brand-creation-prepress.webp",
      accent: "var(--ap-cyan)",
      Icon: PencilRuler,
      bird: BIRDS.bird_solo,
    },
    {
      tag: "02 / Press",
      title: t("studio.s2_title"),
      body: t("studio.s2_body"),
      tex: "/images/studio/offset-digital-printing.webp",
      accent: "var(--ap-magenta)",
      Icon: Layers,
      bird: BIRDS.bird_soar,
    },
    {
      tag: "03 / Large Format",
      title: t("studio.s3_title"),
      body: t("studio.s3_body"),
      tex: "/images/studio/large-format-printing.webp",
      accent: "var(--ap-yellow)",
      Icon: Truck,
      bird: BIRDS.bird_side,
    },
    {
      tag: "04 / Finishing",
      title: t("studio.s4_title"),
      body: t("studio.s4_body"),
      tex: "/images/studio/premium-finishing-binding.webp",
      accent: "var(--ap-gold)",
      Icon: Sparkles,
      bird: BIRDS.bird_pair,
    },
  ];
  return (
    <section
      id="studio"
      data-testid="studio-section"
      className="relative bg-[#F4F4F0] text-black border-t border-black"
    >
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-24 md:py-36">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 mb-14">
          <div className="md:col-span-4">
            <div className="flex items-center gap-4 mb-8">
              <span className="w-10 h-px bg-black" />
              <span className="font-mono text-[10px] tracking-[0.35em] uppercase">
                {t("studio.chapter")}
              </span>
            </div>
            <h2 className="font-display text-5xl md:text-7xl leading-[0.95] tracking-tight">
              {t("studio.title_1")} <br />
              <em className="italic">{t("studio.title_2_italic")}</em>{" "}
              {t("studio.title_3")} <br />
              {t("studio.title_4")}
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 flex items-end">
            <p className="text-base md:text-lg text-black/75 leading-relaxed max-w-[52ch]">
              {t("studio.sub")}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-t border-l border-black">
          {SERVICES.map((s, i) => {
            const { Icon } = s;
            return (
              <motion.a
                key={s.title}
                href="#contact"
                data-testid={`studio-card-${i}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10% 0px" }}
                transition={{ duration: 0.7, delay: i * 0.06 }}
                className="studio-card group border-r border-b border-black bg-[#F4F4F0] p-8 md:p-12 min-h-[420px] flex flex-col justify-between"
              >
                <div
                  className="tex"
                  style={{ backgroundImage: `url(${s.tex})` }}
                />
                <div
                  className="tex"
                  style={{
                    background: `linear-gradient(180deg, transparent 0%, ${s.accent} 300%)`,
                    mixBlendMode: "multiply",
                    opacity: 0,
                  }}
                />

                <div className="studio-inner flex items-start justify-between gap-6">
                  <div>
                    <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-black/60 group-hover:text-[#F4F4F0]/80 transition-colors">
                      {s.tag}
                    </div>
                    <div className="mt-3 flex items-center gap-3">
                      <Icon className="w-5 h-5" />
                      <span className="font-mono text-[11px] tracking-[0.25em] uppercase">
                        {t("studio.discipline")}
                      </span>
                    </div>
                  </div>
                  <img
                    src={s.bird}
                    alt=""
                    aria-hidden
                    className="w-14 h-14 object-contain opacity-90 group-hover:rotate-[8deg] transition-transform duration-700"
                  />
                </div>

                <div className="studio-inner mt-10">
                  <h3 className="font-display text-3xl md:text-5xl leading-[1.02] tracking-tight">
                    {s.title}
                  </h3>
                  <p className="mt-4 text-sm md:text-base leading-relaxed max-w-[46ch] opacity-90">
                    {s.body}
                  </p>
                  <div className="mt-6 flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase">
                    <span className="hairline block w-8 h-px" />
                    {t("studio.cta")} <ArrowUpRight className="w-3.5 h-3.5" />
                  </div>
                </div>
              </motion.a>
            );
          })}
        </div>
      </div>
    </section>
  );
}
